import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { WorkflowStepActionKey } from 'dashboard/models/workflow-version';
import { ApiModel } from 'lib/interfaces/api';

type ReportMapping = {
  label: string;
  reportType: Report['type'];
  reportTemplateType: ReportTemplate['type'];
  // workflowStepActionKey is nullable because not
  // every report can be created via a workflow step
  workflowStepActionKey?: WorkflowStepActionKey;
  fields: string[];
};

export const REPORT_MAPPINGS: ReportMapping[] = [
  {
    label: 'Report',
    reportType: ApiModel.Report,
    reportTemplateType: ApiModel.ReportTemplateAddressLookup,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowStepActionKey: null,
    fields: [],
  },
  {
    label: 'Address Lookup',
    reportType: ApiModel.ReportAddressLookup,
    reportTemplateType: ApiModel.ReportTemplateAddressLookup,
    workflowStepActionKey: WorkflowStepActionKey.RunReportAddressLookup,
    fields: [
      'address-street-1',
      'address-street-2',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
    ],
  },
  {
    label: 'Adverse Media',
    reportType: ApiModel.ReportAdverseMedia,
    reportTemplateType: ApiModel.ReportTemplateAdverseMedia,
    workflowStepActionKey: WorkflowStepActionKey.RunReportAdverseMedia,
    fields: ['name-first', 'name-middle', 'name-last', 'birthdate', 'country-code'],
  },
  {
    label: 'Business Adverse Media',
    reportType: ApiModel.ReportBusinessAdverseMedia,
    reportTemplateType: ApiModel.ReportTemplateBusinessAdverseMedia,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessAdverseMedia,
    fields: ['term', 'country-code'],
  },
  {
    label: 'Business Associated Persons',
    reportType: ApiModel.ReportBusinessAssociatedPersons,
    reportTemplateType: ApiModel.ReportTemplateBusinessAssociatedPersons,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
    fields: ['address-country-code'],
  },
  {
    label: 'Business Classification',
    reportType: ApiModel.ReportBusinessClassification,
    reportTemplateType: ApiModel.ReportTemplateBusinessClassification,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessClassification,
    fields: [
      'business-name',
      'website',
      'address-street-1',
      'address-street-2',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
    ],
  },
  {
    label: 'Business Industry Classification',
    reportType: ApiModel.ReportBusinessIndustryClassification,
    reportTemplateType: ApiModel.ReportTemplateBusinessIndustryClassification,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowStepActionKey: null,
    fields: ['business-name', 'website'],
  },
  {
    label: 'Business Liens',
    reportType: ApiModel.ReportBusinessLiensLookup,
    reportTemplateType: ApiModel.ReportTemplateBusinessLiensLookup,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessLiensLookup,
    fields: ['address-country-code'],
  },
  {
    label: 'Business Lookup',
    reportType: ApiModel.ReportBusinessLookup,
    reportTemplateType: ApiModel.ReportTemplateBusinessLookup,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessLookup,
    fields: [
      'phone-number',
      'business-name',
      'address-street-1',
      'address-street-2',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'incorporation-state',
      'website',
      'associated-people-name-first',
      'associated-people-name-last',
    ],
  },
  {
    label: 'Business Nonprofit',
    reportType: ApiModel.ReportBusinessNonprofit,
    reportTemplateType: ApiModel.ReportTemplateBusinessNonprofit,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessNonprofit,
    fields: ['ein'],
  },
  {
    label: 'Business Online Presence',
    reportType: ApiModel.ReportBusinessOnlinePresence,
    reportTemplateType: ApiModel.ReportTemplateBusinessOnlinePresence,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessOnlinePresence,
    fields: [
      'business-name',
      'website',
      'address-street-1',
      'address-street-2',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
    ],
  },
  {
    label: 'Business Registrations',
    reportType: ApiModel.ReportBusinessRegistrationsLookup,
    reportTemplateType: ApiModel.ReportTemplateBusinessRegistrationsLookup,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessRegistrationsLookup,
    fields: ['address-country-code'],
  },
  {
    label: 'Business Social Media',
    reportType: ApiModel.ReportBusinessSocialMedia,
    reportTemplateType: ApiModel.ReportTemplateBusinessSocialMedia,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessSocialMedia,
    fields: ['name', 'ticker', 'profile', 'website'],
  },
  {
    label: 'Business Watchlist',
    reportType: ApiModel.ReportBusinessWatchlist,
    reportTemplateType: ApiModel.ReportTemplateBusinessWatchlist,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessWatchlist,
    fields: ['term', 'country-code'],
  },
  {
    label: 'Business Website',
    reportType: ApiModel.ReportBusinessWebsite,
    reportTemplateType: ApiModel.ReportTemplateBusinessWebsite,
    workflowStepActionKey: WorkflowStepActionKey.RunReportBusinessWebsite,
    fields: ['business-name', 'website'],
  },
  {
    label: 'Chainalysis Address Screening',
    reportType: ApiModel.ReportChainalysisAddressScreening,
    reportTemplateType: ApiModel.ReportTemplateChainalysisAddressScreening,
    workflowStepActionKey: WorkflowStepActionKey.ChainalysisAddressScreening,
    fields: ['address'],
  },
  {
    label: 'Clearbit Business Lookup',
    reportType: ApiModel.ReportClearbitBusinessLookup,
    reportTemplateType: ApiModel.ReportTemplateClearbitBusinessLookup,
    workflowStepActionKey: WorkflowStepActionKey.ClearbitBusinessLookup,
    fields: [],
  },
  {
    label: 'Coinbase Check Crypto Risk',
    reportType: ApiModel.ReportCoinbaseCheckCryptoRisk,
    reportTemplateType: ApiModel.ReportTemplateCoinbaseCheckCryptoRisk,
    workflowStepActionKey: WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    fields: [],
  },
  {
    label: 'Crypto Address Watchlist',
    reportType: ApiModel.ReportCryptoAddressWatchlist,
    reportTemplateType: ApiModel.ReportTemplateCryptoAddressWatchlist,
    workflowStepActionKey: WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    fields: ['crypto-address'],
  },
  {
    label: 'Custom List',
    reportType: ApiModel.ReportCustomList,
    reportTemplateType: ApiModel.ReportTemplateCustomList,
    workflowStepActionKey: WorkflowStepActionKey.RunReportCustomList,
    fields: [
      'name_first',
      'name_middle',
      'name_last',
      'name_suffix',
      'name_full',
      'name_alias_first',
      'name_alias_middle',
      'name_alias_last',
      'name_alias_suffix',
      'sex',
      'business_name',
      'business_dba',
      'phone_number',
      'address_street_1',
      'address_street_2',
      'address_city',
      'address_subdivision',
      'address_postal_code',
      'address_country_code',
      'address_full',
      'birthdate',
      'incorporation_date',
      'identification_number',
      'identification_number_type',
      'drivers_license',
      'passport',
      'alien_registration',
      'social_security_number',
      'employer_identification_number',
      'taxpayer_identification_number',
      'email_address',
      'nationality',
    ],
  },
  {
    label: 'Email Address',
    reportType: ApiModel.ReportEmailAddress,
    reportTemplateType: ApiModel.ReportTemplateEmailAddress,
    workflowStepActionKey: WorkflowStepActionKey.RunReportEmailAddress,
    fields: ['email-address'],
  },
  {
    label: 'Equifax Oneview',
    reportType: ApiModel.ReportEquifaxOneview,
    reportTemplateType: ApiModel.ReportTemplateEquifaxOneview,
    workflowStepActionKey: WorkflowStepActionKey.EquifaxOneview,
    fields: [
      // required fields
      'name-first',
      'name-last',
      'address-city',
      'address-state',

      // optional fields
      'address-street-1',
      'address-zip',
      'birthdate',
      'name-middle',
      'name-suffix',
      'social-security-number',
      'reference-id',
    ],
  },
  {
    label: 'FINRA Broker Check',
    reportType: ApiModel.ReportFinraBrokerCheck,
    reportTemplateType: ApiModel.ReportTemplateFinraBrokerCheck,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowStepActionKey: null,
    fields: [],
  },
  {
    label: 'Kyckr Business Lookup',
    reportType: ApiModel.ReportKyckrBusinessLookup,
    reportTemplateType: ApiModel.ReportTemplateKyckrBusinessLookup,
    workflowStepActionKey: WorkflowStepActionKey.RunKyckrBusinessLookup,
    fields: [
      // required fields
      'country-code',

      // optional fields
      'company-name',
      'company-number',
      'order-ref',
      'registration-authority',
    ],
  },
  {
    label: 'Middesk Business Lookup',
    reportType: ApiModel.ReportMiddeskBusinessLookup,
    reportTemplateType: ApiModel.ReportTemplateMiddeskBusinessLookup,
    workflowStepActionKey: WorkflowStepActionKey.SubmitMiddeskBusinessLookup,
    fields: [
      // required fields
      'business-name',
      'address-street-1',
      'address-street-2',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',

      // optional fields
      'phone-number',
      'tin',
      'website',
    ],
  },
  {
    label: 'MX Account',
    reportType: ApiModel.ReportMxAccount,
    reportTemplateType: ApiModel.ReportTemplateMxAccount,
    workflowStepActionKey: WorkflowStepActionKey.MxAccount,
    fields: ['business-name', 'name-first', 'name-middle', 'name-last', 'user-guid', 'member-guid'],
  },
  {
    label: 'Phone Number',
    reportType: ApiModel.ReportPhoneNumber,
    reportTemplateType: ApiModel.ReportTemplatePhoneNumber,
    workflowStepActionKey: WorkflowStepActionKey.RunReportPhoneNumber,
    fields: ['phone-number'],
  },
  {
    label: 'Politically Exposed Person',
    reportType: ApiModel.ReportPoliticallyExposedPerson,
    reportTemplateType: ApiModel.ReportTemplatePoliticallyExposedPerson,
    workflowStepActionKey: WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
    fields: [
      'name-first',
      'name-middle',
      'name-last',
      'birthdate',
      'country-code',
      'comparison-photo',
    ],
  },
  {
    label: 'Profile',
    reportType: ApiModel.ReportProfile,
    reportTemplateType: ApiModel.ReportTemplateProfile,
    workflowStepActionKey: WorkflowStepActionKey.RunReportProfile,
    fields: [
      'name-first',
      'name-last',
      'birthdate',
      'social-security-number',
      'address-street-1',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'country-code',
      'phone-number',
    ],
  },
  {
    label: 'Profile Non-Authoritative',
    reportType: ApiModel.ReportProfileNonAuthoritative,
    reportTemplateType: ApiModel.ReportTemplateProfileNonAuthoritative,
    workflowStepActionKey: WorkflowStepActionKey.RunReportProfileNonAuthoritative,
    fields: [
      'name-first',
      'name-last',
      'address-city',
      'address-subdivision',
      'phone-number',
      'email-address',
    ],
  },
  {
    label: 'Screening',
    reportType: ApiModel.ReportScreening,
    reportTemplateType: ApiModel.ReportTemplateScreening,
    workflowStepActionKey: WorkflowStepActionKey.RunReportScreening,
    fields: [
      'name-first',
      'name-middle',
      'name-last',
      'address-street-1',
      'address-city',
      'address-subdivision',
      'address-country_code',
    ],
  },
  {
    label: 'SEC Action Lookup',
    reportType: ApiModel.ReportSecActionLookup,
    reportTemplateType: ApiModel.ReportTemplateSecActionLookup,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowStepActionKey: null,
    fields: [],
  },
  {
    label: 'Sentilink Application Risk',
    reportType: ApiModel.ReportSentilinkApplicationRisk,
    reportTemplateType: ApiModel.ReportTemplateSentilinkApplicationRisk,
    workflowStepActionKey: WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    fields: [
      'user-id',
      'name-first',
      'name-last',
      'birthdate',
      'social-security-number',
      'address-street-1',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
      'phone-number',
      'email-address',
      'ip-address',
    ],
  },
  {
    label: 'SentiLink Scores',
    reportType: ApiModel.ReportSentilinkScores,
    reportTemplateType: ApiModel.ReportTemplateSentilinkScores,
    workflowStepActionKey: WorkflowStepActionKey.RunReportSentilinkScores,
    fields: [
      'user-id',
      'name-first',
      'name-last',
      'birthdate',
      'social-security-number',
      'address-street-1',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
      'phone-number',
      'email-address',
    ],
  },
  {
    label: 'Snapshot Adverse Media',
    reportType: ApiModel.ReportSnapshotAdverseMedia,
    reportTemplateType: ApiModel.ReportTemplateAdverseMedia,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowStepActionKey: null,
    fields: ['name-first', 'name-middle', 'name-last', 'birthdate', 'country-code'],
  },
  {
    label: 'Snapshot Watchlist',
    reportType: ApiModel.ReportSnapshotWatchlist,
    reportTemplateType: ApiModel.ReportTemplateWatchlist,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowStepActionKey: null,
    fields: [
      'name-first',
      'name-middle',
      'name-last',
      'birthdate',
      'country-code',
      'profile-image',
      'comparison-photo',
    ],
  },
  {
    label: 'Social Media',
    reportType: ApiModel.ReportSocialMedia,
    reportTemplateType: ApiModel.ReportTemplateSocialMedia,
    workflowStepActionKey: WorkflowStepActionKey.RunReportSocialMedia,
    fields: [
      'name-first',
      'name-last',
      'birthdate',
      'address-street-1',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
      'phone-number',
      'email-address',
    ],
  },
  {
    label: 'Synthetic',
    reportType: ApiModel.ReportSynthetic,
    reportTemplateType: ApiModel.ReportTemplateSynthetic,
    workflowStepActionKey: WorkflowStepActionKey.RunReportSynthetic,
    fields: [
      'name-first',
      'name-last',
      'birthdate',
      'social-security-number',
      'address-street-1',
      'address-city',
      'address-subdivision',
      'address-postal-code',
      'address-country-code',
      'phone-number',
      'email-address',
    ],
  },
  {
    label: 'TRM Wallet Screening',
    reportType: ApiModel.ReportTrmWalletScreening,
    reportTemplateType: ApiModel.ReportTemplateTrmWalletScreening,
    workflowStepActionKey: WorkflowStepActionKey.TrmWalletScreening,
    fields: ['address', 'chain', 'account-external-id'],
  },
  {
    label: 'Watchlist',
    reportType: ApiModel.ReportWatchlist,
    reportTemplateType: ApiModel.ReportTemplateWatchlist,
    workflowStepActionKey: WorkflowStepActionKey.RunReportWatchlist,
    fields: [
      'name-first',
      'name-middle',
      'name-last',
      'birthdate',
      'country-code',
      'profile-image',
      'comparison-photo',
    ],
  },
];
